import React, { useEffect, useMemo, useRef, useState } from "react";
import { useClient, useQuery } from "urql";
import { sortedLastIndex, sumBy } from "lodash";
import { Trash2 } from "lucide-react";

import { DateHelper } from "../../../../common/src";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { useQueryState } from "../../../../components/src/use-query-state";

import { KLEER_COMPANY_INFO_QUERY, KLEER_ACCOUNT_METADATA_QUERY } from "../../../../graphql/src/external/kleer_queries";

import { getNavs, perenneFundId } from "../AumQuarterlyPage";

import "./IncomeStatementPage.css";

import type { KleerCompanyInfo } from "../../types.generated";

const navy = "#075985";
const muted = "#6b7785";
const costColor = "#c62828";
const profitColor = "#2e7d32";

const numberFormat = new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 });
const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// The business assumptions of the page, in one place. They are constants in the code and are not saved
const assumptions = {
    // The AIF funds. Perenne is a UCITS fund that is shown on its own
    aifFundIds: ["684a93694509026c2bdb2e3a"],

    // A month that ended less than this many months ago is preliminary (PRE), older months are actual (ACT). The current month and the
    // following months are forecast
    actualLagMonths: 1,

    // The default AUM growth per month in MSEK for the forecast months, when the AUM is not edited
    aumGrowthPerMonth: { ucits: 100, perenne: 100, aif: 1 },

    // Revenue rates of the forecast. AIF is per year. UCITS is the rate of the closed months plus the margin. Perenne is not taken from
    // the accounts, it is the average AUM of the month and the month before times the rate, and it is deducted from UCITS
    aifRate: 0.008,
    ucitsRateMargin: 0.00005,
    perenneRate: 0.0000394,

    // The total salary cost of the year including social fees in SEK, and the share of it that falls on each month in percent, a lower
    // share in July for the holidays
    annualSalary: 16395283,
    personnelKey: [8.87, 8.87, 8.87, 8.87, 8.87, 8.87, 4.59, 7.97, 8.87, 8.87, 8.87, 7.59],

    // The equity at the start of a year in SEK. The equity of a month is the equity of the month before plus the result and the
    // contribution of the month. The year after the latest year here starts with the equity at the end of the latest year
    openingEquity: { 2026: 8469296 } as Record<number, number>,

    // The capital requirement of a year in SEK, the same in every month. The year after the latest year here has the requirement of the
    // latest year per cost, times its own costs. The capital contribution of a month is edited in the table, 0 by default
    capitalRequirements: { 2026: 6042635 } as Record<number, number>,

    // A year ahead is the result of this year scaled by these default rates per year, in percent
    defaultRevenueGrowth: 10,
    defaultCostGrowth: 5,

    // The first year in the year menu. The menu goes from this year up to two years ahead of the current year, so a new year appears at
    // the end when a new year starts. Raise the first year to remove old years
    firstYear: 2025,

    // Accounts that are not part of the income statement. 8999 Årets resultat is the closing of the accounting year, booked in December
    excludedAccountNrs: [8999]
};
const {
    aifFundIds,
    actualLagMonths,
    aumGrowthPerMonth,
    aifRate,
    ucitsRateMargin,
    perenneRate,
    annualSalary,
    personnelKey,
    openingEquity,
    capitalRequirements,
    defaultRevenueGrowth,
    defaultCostGrowth,
    firstYear,
    excludedAccountNrs
} = assumptions;
const aumFunds = ["ucits", "perenne", "aif"];

const Revenue = "Revenue";
const Costs = "Costs";

// The rules of the forecast, per category. All averages are of actual months only, never preliminary
//   UCITS           rate = net UCITS revenue per average AUM of the closed months, plus a margin. Revenue = effective AUM x rate
//   Perenne         effective AUM x the Perenne rate, every month. Deducted from UCITS
//   AIF             effective AUM x the AIF rate / 12
//   Other           the value of the latest actual month, flat
//   Activations     the average of the last three actual months. Booked Activations of a quarter are spread over its three months
//   Custodian       the average of the last three actual months. A month with 0 gets the latest value
//   Other external  the average of the last three actual months
//   Personnel       the annual salary times the key of the month
//   Depreciation    the average of the last three actual months
//   Financial       the average of all actual months of the year
// Effective AUM is the average of the AUM of the month before and of the month, and January has only its own.
// Categories in display order. An account belongs to the first category that lists its number
const categories = [
    { group: Revenue, label: "UCITS", accountNrs: [3041, 3043, 3044, 3045, 3048, 3049, 3740, 3730, 6010] },
    { group: Revenue, label: "Perenne", accountNrs: [] as number[] },
    { group: Revenue, label: "AIF", accountNrs: [3050] },
    { group: Revenue, label: "Other", accountNrs: [3001] },
    { group: Revenue, label: "Activations", accountNrs: [3850, 3870] },
    { group: Costs, label: "Custodian", accountNrs: [4290, 4450] },
    {
        group: Costs,
        label: "Other external",
        accountNrs: [
            5010, 5090, 5211, 5410, 5420, 5421, 5460, 5700, 5800, 5830, 5900, 5910, 5940, 5980, 6072, 6079, 6110, 6212, 6300, 6420, 6490,
            6530, 6540, 6550, 6560, 6570, 6580, 6590, 6950, 6970, 6981, 6982, 6991, 6992, 6993
        ]
    },
    {
        group: Costs,
        label: "Personnel",
        accountNrs: [7210, 7211, 7290, 7299, 7331, 7389, 7399, 7410, 7510, 7519, 7530, 7580, 7610, 7632, 7690, 7699]
    },
    { group: Costs, label: "Depreciation", accountNrs: [7810] },
    { group: Costs, label: "Financial", accountNrs: [3960, 7960, 8223, 8311, 8313, 8314, 8330, 8350, 8423] }
];

interface KleerAccount {
    accountNr: number;
    description?: string;
    inBalance?: number;
    outBalance?: number;
}

// A row of the statement. The amounts are signed as shown: revenue and a profit are positive, a cost and a loss are negative
interface StatementRow {
    kind: "group" | "category" | "account" | "sum" | "result" | "accumulated" | "volume" | "volumeSum" | "capital" | "capitalSurplus";
    label: string;
    // The amount of each month, undefined for a month without data. A group row has none
    values?: (number | undefined)[];
    // The amount of each month before a manual adjustment, for the months that are adjusted
    originals?: (number | undefined)[];
    total?: number;
    // The fund of an AUM row
    fund?: string;
    // The year, group and label of a category row, to find its manual adjustments and to expand it
    categoryKey?: string;
    expanded?: boolean;
}

type MonthStatus = "actual" | "preliminary" | "forecast";

// One entry per month, for the ids of the columns and to make a list with a place for each month
const monthKeys = monthNames.map((_name, index) => `m${index}`);

const forecastColor = "#2563eb";
const adjustmentColor = "#ea580c";
const adjustmentBackground = "#fff4e6";
const preliminaryColor = "#d97706";

// A number in a table cell that looks like text with a dotted underline and can be edited. Enter or click outside saves, an empty field
// resets to the default (null) and a value that is not a number is dropped
const EditableNumber = ({
    value,
    title,
    style,
    onCommit
}: {
    value: number;
    title: string;
    style: React.CSSProperties;
    onCommit: (value: number | null) => void;
}): React.ReactElement => (
    <input
        type="text"
        inputMode="numeric"
        defaultValue={numberFormat.format(value)}
        title={title}
        className="editable-number"
        style={style}
        onFocus={(event) => {
            event.currentTarget.style.background = "#eff6ff";
            event.currentTarget.select();
        }}
        onBlur={(event) => {
            event.currentTarget.style.background = "transparent";
            const entered = event.target.value.replace(/[^0-9,.-]/g, "").replace(",", ".");
            if (entered === "") {
                onCommit(null);
            } else if (!isNaN(Number(entered)) && Number(entered) !== Math.round(value)) {
                onCommit(Number(entered));
            } else {
                event.target.value = numberFormat.format(value);
            }
        }}
        onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
        }}
    />
);

// A cell of a category row that can be adjusted by hand. Click to edit, Enter or click outside to save, Esc to cancel. An adjusted cell is
// orange, with the original value in the tooltip and a cross to remove the adjustment
const AdjustableCell = ({
    text,
    style,
    adjusted,
    originalText,
    currentValue,
    onCommit,
    onClear
}: {
    text: string;
    style: React.CSSProperties;
    adjusted: boolean;
    originalText: string;
    currentValue: number;
    onCommit: (value: number) => void;
    onClear: () => void;
}): React.ReactElement => {
    const [editing, setEditing] = useState<boolean>(false);
    const [hovering, setHovering] = useState<boolean>(false);
    const cancelled = useRef<boolean>(false);

    if (editing) {
        return (
            <input
                autoFocus
                type="text"
                inputMode="numeric"
                defaultValue={String(Math.round(currentValue))}
                style={{
                    ...style,
                    width: "100%",
                    padding: 0,
                    font: "inherit",
                    fontStyle: "normal",
                    color: adjustmentColor,
                    background: adjustmentBackground,
                    border: 0,
                    borderBottom: `1px solid ${adjustmentColor}`,
                    outline: "none"
                }}
                onFocus={(event) => event.currentTarget.select()}
                onBlur={(event) => {
                    setEditing(false);
                    if (cancelled.current) {
                        cancelled.current = false;
                        return;
                    }

                    // An empty field cancels and keeps an existing adjustment
                    const entered = event.target.value.replace(/[^0-9,.-]/g, "").replace(",", ".");
                    if (entered !== "" && !isNaN(Number(entered))) onCommit(Number(entered));
                }}
                onKeyDown={(event) => {
                    if (event.key === "Enter") event.currentTarget.blur();
                    if (event.key === "Escape") {
                        cancelled.current = true;
                        event.currentTarget.blur();
                    }
                }}
            />
        );
    }

    const cellStyle: React.CSSProperties = { ...style, cursor: "pointer", position: "relative" };
    if (adjusted) {
        cellStyle.color = adjustmentColor;
        cellStyle.background = adjustmentBackground;
        cellStyle.fontStyle = "normal";
    }
    return (
        <span
            title={adjusted ? `Manual adjustment — original: ${originalText}` : "Click to adjust"}
            style={cellStyle}
            onClick={() => setEditing(true)}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
        >
            {adjusted && hovering && (
                <button
                    type="button"
                    title="Remove the adjustment"
                    onClick={(event) => {
                        event.stopPropagation();
                        onClear();
                    }}
                    style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        padding: "0 3px",
                        border: 0,
                        background: "transparent",
                        color: adjustmentColor
                    }}
                >
                    ×
                </button>
            )}
            {text}
        </span>
    );
};

// A record of numbers that is kept in the browser's localStorage under the key. An empty record is not stored. Without storage (a private
// window, blocked site data) it lasts until the page is reloaded
const useStoredState = (storageKey: string): [Record<string, number>, React.Dispatch<React.SetStateAction<Record<string, number>>>] => {
    const [state, setState] = useState<Record<string, number>>(() => {
        try {
            return JSON.parse(localStorage.getItem(storageKey) ?? "{}");
        } catch {
            return {};
        }
    });

    useEffect(() => {
        try {
            if (Object.keys(state).length > 0) localStorage.setItem(storageKey, JSON.stringify(state));
            else localStorage.removeItem(storageKey);
        } catch {
            // Storage is not available
        }
    }, [storageKey, state]);

    return [state, setState];
};

export function IncomeStatementPage(): React.ReactElement {
    const todaysDate = DateHelper.dateToString(DateHelper.today()).substring(0, 10);
    const thisYear = Number(todaysDate.substring(0, 4));
    const thisMonth = Number(todaysDate.substring(5, 7)) - 1;

    // From the first year to this year and the two years ahead
    const selectableYears = Array.from({ length: thisYear + 2 - firstYear + 1 }, (_value, index) => firstYear + index);
    const [yearString, setYearString] = useQueryState("year", String(thisYear));
    const year = selectableYears.includes(Number(yearString)) ? Number(yearString) : thisYear;

    // A year ahead has no data of its own. It is calculated from this year, so the data is loaded for the year of the data
    const dataYear = Math.min(year, thisYear);
    const yearsAhead = Math.max(0, year - thisYear);
    const [revenueGrowth, setRevenueGrowth] = useState<number>(defaultRevenueGrowth);
    const [costGrowth, setCostGrowth] = useState<number>(defaultCostGrowth);

    const client = useClient();

    const [{ data: dataInfo, error: errorInfo }] = useQuery({
        query: KLEER_COMPANY_INFO_QUERY,
        requestPolicy: "cache-and-network"
    });

    // Actual until the first day of the month that is the lag before this month, preliminary after that. The current month is forecast
    // until its last day, from then on its data is taken from Kleer
    // The statuses of the shown year and of the year of the data. A year ahead is all forecast
    const [monthStatuses, dataStatuses] = useMemo((): MonthStatus[][] => {
        const firstActualCutoff = new Date(Date.UTC(thisYear, thisMonth - actualLagMonths, 1)).toISOString().substring(0, 10);
        return [year, dataYear].map((statusYear) =>
            monthNames.map((_name, index) => {
                const monthEnd = new Date(Date.UTC(statusYear, index + 1, 0)).toISOString().substring(0, 10);
                if (monthEnd > todaysDate) return "forecast";
                if (monthEnd < firstActualCutoff) return "actual";
                return "preliminary";
            })
        );
    }, [year, dataYear, thisYear, thisMonth, todaysDate]);

    // The accounts of each month, null for a forecast month
    const [monthAccounts, setMonthAccounts] = useState<(KleerAccount[] | null)[]>([]);
    const [fetching, setFetching] = useState<boolean>(true);
    const [error, setError] = useState<unknown>(null);

    // Edited AUM of the forecast months in MSEK, by year, fund and month index, and the edited contributions. Saved in the browser
    const [aumEdits, setAumEdits] = useStoredState("incomeStatementAumEdits");

    // Manual adjustments of the category cells, by year, category and month index, as shown on the page (a cost has a minus sign). Saved in
    // the browser, like the AUM edits, until they are reset with the Reset changes button
    const [manualAdjustments, setManualAdjustments] = useStoredState("incomeStatementAdjustments");
    const [confirmingClear, setConfirmingClear] = useState<boolean>(false);
    // Only the changes of the shown year count for the legend and the Reset button
    const hasAdjustments = Object.keys(manualAdjustments).some((key) => key.startsWith(`${year}|`));
    const hasChanges = hasAdjustments || Object.keys(aumEdits).some((key) => key.startsWith(`${year}|`));

    // The category rows that are expanded to show their accounts, by category key
    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

    useEffect(() => {
        let cancelled = false;
        setFetching(true);
        setError(null);

        Promise.all(
            monthNames.map((_name, index) => {
                if (dataStatuses[index] === "forecast") return Promise.resolve(null);

                const start = new Date(Date.UTC(dataYear, index, 1)).toISOString().substring(0, 10);
                const end = new Date(Date.UTC(dataYear, index + 1, 0)).toISOString().substring(0, 10);
                return client
                    .query(KLEER_ACCOUNT_METADATA_QUERY, { startDate: start, endDate: end }, { requestPolicy: "cache-and-network" })
                    .toPromise();
            })
        ).then((results) => {
            if (cancelled) return;

            const failed = results.find((r) => r && r.error);
            if (failed) {
                setError(failed.error);
            } else {
                setMonthAccounts(results.map((r) => (r ? (r.data?.kleerAccountMetadata ?? []) : null)));
            }
            setFetching(false);
        });

        return () => {
            cancelled = true;
        };
    }, [client, dataYear, dataStatuses]);

    const [{ data: dataNavs, error: errorNavs }] = useQuery({ query: getNavs, variables: { startDate: `${dataYear - 1}-12-15` } });

    const kleerCompanyInfo: KleerCompanyInfo = dataInfo && dataInfo.kleerCompanyInfo ? dataInfo.kleerCompanyInfo : {};

    const rows = useMemo((): StatementRow[] => {
        // A forecast month has values from the forecast, the other months when their accounts are loaded
        const hasData = dataStatuses.map((status, index) => status === "forecast" || !!monthAccounts[index]);

        // The amount per category and month, and the result per month. The result is added up from the groups below
        const amounts: Record<string, (number | undefined)[]> = {};
        const accountDetails: Record<string, Record<number, { label: string; values: (number | undefined)[] }>> = {};
        const results: (number | undefined)[] = monthKeys.map((_key, index) => (hasData[index] ? 0 : undefined));

        monthAccounts.forEach((accounts, monthIndex) => {
            if (!accounts || dataStatuses[monthIndex] === "forecast") return;

            for (const account of accounts) {
                if (typeof account.accountNr !== "number" || account.accountNr < 3000 || account.accountNr >= 9000) continue;
                if (excludedAccountNrs.includes(account.accountNr)) continue;

                // Kleer amounts are in minor units (öre)
                // The amount is credit minus debit, so revenue is positive and a cost is negative
                const amount = ((account.inBalance ?? 0) - (account.outBalance ?? 0)) / 100;
                if (amount === 0) continue;

                // An account that is in no list is other revenue if its number is below 4000, otherwise Other external
                let group = account.accountNr < 4000 ? Revenue : Costs;
                let label = account.accountNr < 4000 ? "Other" : "Other external";
                const category = categories.find((c) => c.accountNrs.includes(account.accountNr));
                if (category) {
                    group = category.group;
                    label = category.label;
                }
                const key = group + "|" + label;
                amounts[key] = amounts[key] ?? monthKeys.map(() => undefined);
                amounts[key][monthIndex] = (amounts[key][monthIndex] ?? 0) + amount;

                // The accounts of the category as booked in Kleer, to show when the category is expanded
                accountDetails[key] = accountDetails[key] ?? {};
                accountDetails[key][account.accountNr] = accountDetails[key][account.accountNr] ?? {
                    label: account.accountNr + " - " + (account.description ?? ""),
                    values: monthKeys.map(() => undefined)
                };
                const detailValues = accountDetails[key][account.accountNr].values;
                detailValues[monthIndex] = (detailValues[monthIndex] ?? 0) + amount;
            }
        });

        // Activations is booked once a quarter. As soon as there is booked Activations in a quarter it is spread evenly over the three
        // months of the quarter, also the months that are not closed yet
        const activationsKey = Revenue + "|Activations";
        const spreadMonths: number[] = [];
        const activations = amounts[activationsKey];
        if (activations) {
            for (let quarter = 0; quarter < 4; quarter++) {
                const quarterMonths = [quarter * 3, quarter * 3 + 1, quarter * 3 + 2];
                const quarterTotal = quarterMonths.reduce((sum, month) => sum + (activations[month] ?? 0), 0);
                if (quarterTotal === 0) continue;

                spreadMonths.push(...quarterMonths);
                for (const month of quarterMonths) {
                    activations[month] = quarterTotal / 3;
                }
            }
        }

        // Forecast of the categories that are not based on AUM. The averages are of actual months only, never preliminary
        const actualMonths = dataStatuses
            .map((status, index) => (status === "actual" && monthAccounts[index] ? index : -1))
            .filter((i) => i >= 0);
        const forecastMonths = dataStatuses.map((status, index) => (status === "forecast" ? index : -1)).filter((i) => i >= 0);

        // Custodian: an actual or preliminary month with 0 gets the latest value
        const custodian = amounts[Costs + "|Custodian"];
        if (custodian) {
            let latestValue: number | undefined;
            dataStatuses.forEach((status, index) => {
                if (status === "forecast" || !hasData[index]) return;
                if (custodian[index]) {
                    latestValue = custodian[index];
                } else if (typeof latestValue !== "undefined") {
                    custodian[index] = latestValue;
                }
            });
        }

        const averageOf = (key: string, months: number[]): number => {
            if (months.length === 0) return 0;
            return months.reduce((sum, month) => sum + (amounts[key]?.[month] ?? 0), 0) / months.length;
        };

        for (const category of categories) {
            if (["UCITS", "Perenne", "AIF"].includes(category.label)) continue;

            const key = category.group + "|" + category.label;
            if (!amounts[key]) continue;

            let value: number;
            if (category.label === "Other") {
                // The value of the latest actual month, flat
                value = actualMonths.length ? (amounts[key][actualMonths[actualMonths.length - 1]] ?? 0) : 0;
            } else if (category.label === "Financial") {
                value = averageOf(key, actualMonths);
            } else {
                value = averageOf(key, actualMonths.slice(-3));
            }
            for (const month of forecastMonths) {
                // A forecast month in a quarter with booked Activations already has its share of the quarter
                if (category.label === "Activations" && spreadMonths.includes(month)) continue;
                amounts[key][month] = value;
            }
        }

        // Personnel of the forecast months from the salary cost of the year and the key of the month
        const personalKey = Costs + "|Personnel";
        amounts[personalKey] = amounts[personalKey] ?? monthKeys.map(() => undefined);
        for (const month of forecastMonths) {
            amounts[personalKey][month] = -(annualSalary * personnelKey[month]) / 100;
        }

        const result: StatementRow[] = [];
        const aheadAumAmounts: Record<string, number[]> = {};

        // AUM in MSEK at the end of each actual and preliminary month, the NAV on or before that date. The current month has the current
        // AUM. The months after it have the edited AUM, or the month before plus the default growth
        if (dataNavs) {
            const aumByFund: Record<string, { dates: string[]; aums: number[] }> = {};
            const navsByFundAndDate: Record<string, (typeof dataNavs.navs)[number]> = {};
            for (const nav of dataNavs.navs) {
                // One NAV per date, a locked NAV before an active one
                const key = nav.clientId + "|" + nav.date;
                if (!navsByFundAndDate[key] || nav.status === "Locked") navsByFundAndDate[key] = nav;
            }
            for (const nav of Object.values(navsByFundAndDate)) {
                aumByFund[nav.clientId] = aumByFund[nav.clientId] ?? { dates: [], aums: [] };
                aumByFund[nav.clientId].dates.push(nav.date);
            }
            for (const [fundId, fund] of Object.entries(aumByFund)) {
                fund.dates.sort();
                fund.aums = fund.dates.map((date) =>
                    sumBy(
                        navsByFundAndDate[fundId + "|" + date].data.classes,
                        (c: { navAfterAdjustmentsAfterOrders: string; fxRate: string }) =>
                            (Number(c.navAfterAdjustmentsAfterOrders) * Number(c.fxRate)) / 1000000.0
                    )
                );
            }

            const aumAt = (fundId: string, date: string): number => {
                const fund = aumByFund[fundId];
                if (!fund) return 0;
                const index = sortedLastIndex(fund.dates, date) - 1;
                return index < 0 ? 0 : fund.aums[index];
            };

            const fundAumAt = (fund: string, date: string): number => {
                if (fund === "perenne") return aumAt(perenneFundId, date);
                if (fund === "aif") return sumBy(aifFundIds, (fundId) => aumAt(fundId, date));

                let ucits = 0;
                for (const fundId of Object.keys(aumByFund)) {
                    if (fundId !== perenneFundId && !aifFundIds.includes(fundId)) ucits += aumAt(fundId, date);
                }
                return ucits;
            };

            const aums: Record<string, number[]> = { ucits: [], perenne: [], aif: [] };
            monthKeys.forEach((_key, index) => {
                for (const fund of aumFunds) {
                    const monthStart = new Date(Date.UTC(dataYear, index, 1)).toISOString().substring(0, 10);
                    if (dataStatuses[index] !== "forecast") {
                        const monthEnd = new Date(Date.UTC(dataYear, index + 1, 0)).toISOString().substring(0, 10);
                        aums[fund][index] = fundAumAt(fund, monthEnd);
                    } else if (monthStart <= todaysDate) {
                        // The current month has the current AUM
                        aums[fund][index] = fundAumAt(fund, todaysDate);
                    } else if (typeof aumEdits[`${dataYear}|${fund}|${index}`] !== "undefined") {
                        aums[fund][index] = aumEdits[`${dataYear}|${fund}|${index}`];
                    } else {
                        const previous = index > 0 ? aums[fund][index - 1] : fundAumAt(fund, todaysDate);
                        aums[fund][index] = previous + aumGrowthPerMonth[fund];
                    }
                }
            });

            // The AUM of a month is the average of the month before and the month, and January has only its own
            const effectiveAum = (fund: string, index: number): number =>
                index > 0 ? (aums[fund][index - 1] + aums[fund][index]) / 2 : aums[fund][index];

            // Perenne revenue is the effective AUM times a fixed rate, in SEK. An actual or preliminary month has the amount deducted from
            // UCITS, which is booked on the UCITS accounts together with Perenne
            const perenneKey = Revenue + "|Perenne";
            const ucitsKey = Revenue + "|UCITS";
            amounts[perenneKey] = amounts[perenneKey] ?? monthKeys.map(() => undefined);
            amounts[ucitsKey] = amounts[ucitsKey] ?? monthKeys.map(() => undefined);
            monthKeys.forEach((_key, index) => {
                if (!hasData[index]) return;

                const perenneRevenue = effectiveAum("perenne", index) * perenneRate * 1000000;
                amounts[perenneKey][index] = perenneRevenue;
                if (dataStatuses[index] !== "forecast") {
                    amounts[ucitsKey][index] = (amounts[ucitsKey][index] ?? 0) - perenneRevenue;
                }
            });

            // UCITS forecast: the rate is the net UCITS revenue of the actual and preliminary months per average AUM of those months (not the
            // effective AUM) and year, plus a margin
            let forecastRate: number | undefined;
            const closedMonths = dataStatuses
                .map((status, index) => (status !== "forecast" && monthAccounts[index] ? index : -1))
                .filter((i) => i >= 0);
            if (closedMonths.length > 0) {
                const averageNet = closedMonths.reduce((sum, month) => sum + (amounts[ucitsKey][month] ?? 0), 0) / closedMonths.length;
                const averageAum = closedMonths.reduce((sum, month) => sum + aums.ucits[month], 0) / closedMonths.length;
                if (averageAum > 0) {
                    forecastRate = (averageNet / (averageAum * 1000000)) * 12 + ucitsRateMargin;
                    for (const month of forecastMonths) {
                        amounts[ucitsKey][month] = ((effectiveAum("ucits", month) * forecastRate) / 12) * 1000000;
                    }
                }
            }

            // AIF forecast
            const aifKey = Revenue + "|AIF";
            amounts[aifKey] = amounts[aifKey] ?? monthKeys.map(() => undefined);
            for (const month of forecastMonths) {
                amounts[aifKey][month] = ((effectiveAum("aif", month) * aifRate) / 12) * 1000000;
            }

            // A year ahead continues the AUM month by month from the last month of the year before, with the edited AUM or the default
            // growth. Its revenue of UCITS, Perenne and AIF is calculated from that AUM in the same way as the forecast of this year
            const shownAums: Record<string, number[]> = { ucits: aums.ucits, perenne: aums.perenne, aif: aums.aif };
            if (yearsAhead > 0) {
                const effectiveShownAum: Record<string, number[]> = {};
                for (const fund of aumFunds) {
                    let previous = aums[fund][11];
                    for (let step = 1; step <= yearsAhead; step++) {
                        const yearStart = previous;
                        const values = monthKeys.map((_key, index) => {
                            const edit = aumEdits[`${thisYear + step}|${fund}|${index}`];
                            previous = typeof edit !== "undefined" ? edit : previous + aumGrowthPerMonth[fund];
                            return previous;
                        });
                        if (step === yearsAhead) {
                            shownAums[fund] = values;
                            effectiveShownAum[fund] = values.map(
                                (value, index) => ((index > 0 ? values[index - 1] : yearStart) + value) / 2
                            );
                        }
                    }
                }

                // Kept apart until the result of this year is calculated, since the equity of the next year starts with it
                aheadAumAmounts[Revenue + "|Perenne"] = effectiveShownAum.perenne.map((aum) => aum * perenneRate * 1000000);
                aheadAumAmounts[aifKey] = effectiveShownAum.aif.map((aum) => ((aum * aifRate) / 12) * 1000000);
                if (typeof forecastRate !== "undefined") {
                    const rate = forecastRate;
                    aheadAumAmounts[ucitsKey] = effectiveShownAum.ucits.map((aum) => ((aum * rate) / 12) * 1000000);
                }
            }

            // The AUM of the year is the AUM of the last month
            const toVolumeRow = (kind: StatementRow["kind"], label: string, fund: string, values: number[]): StatementRow => {
                return { kind, label, fund, values, total: values[values.length - 1] };
            };
            result.push({ kind: "group", label: "Volume (MSEK)" });
            result.push(toVolumeRow("volume", "AUM UCITS ex Perenne", "ucits", shownAums.ucits));
            result.push(toVolumeRow("volume", "AUM Perenne", "perenne", shownAums.perenne));
            result.push(toVolumeRow("volume", "AUM AIF", "aif", shownAums.aif));
            result.push(
                toVolumeRow(
                    "volumeSum",
                    "Tot AUM",
                    "",
                    shownAums.ucits.map((value, index) => value + shownAums.perenne[index] + shownAums.aif[index])
                )
            );
        }

        // A month with data and nothing booked on the category is 0
        const toRow = (kind: StatementRow["kind"], label: string, values: (number | undefined)[]): StatementRow => ({
            kind,
            label,
            values: values.map((value, index) => value ?? (hasData[index] ? 0 : undefined)),
            total: values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
        });

        // Manual adjustments replace the value of a category and month, after everything else is calculated. The original is kept for
        // the shown year
        const originals: Record<string, number> = {};
        const applyAdjustments = (adjustmentYear: number, keepOriginals: boolean): void => {
            for (const category of categories) {
                const key = category.group + "|" + category.label;
                monthKeys.forEach((_monthKey, index) => {
                    const adjustment = manualAdjustments[`${adjustmentYear}|${key}|${index}`];
                    if (typeof adjustment === "undefined" || !hasData[index]) return;

                    amounts[key] = amounts[key] ?? monthKeys.map(() => undefined);
                    if (keepOriginals) originals[`${adjustmentYear}|${key}|${index}`] = amounts[key][index] ?? 0;
                    amounts[key][index] = adjustment;
                });
            }
        };

        // A year ahead is this year, with its adjustments, scaled by the growth of revenue and the development of costs per year.
        // The adjustments of the shown year come after
        applyAdjustments(dataYear, yearsAhead === 0);

        // The result of the year of the data, to start the equity of the next year with
        let dataYearResult = 0;
        let dataYearCosts = 0;
        for (const [key, values] of Object.entries(amounts)) {
            const total = values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
            dataYearResult += total;
            if (key.startsWith(Costs + "|")) dataYearCosts -= total;
        }
        if (yearsAhead > 0) {
            Object.assign(amounts, aheadAumAmounts);
            for (const category of categories) {
                const key = category.group + "|" + category.label;
                // UCITS, Perenne and AIF follow the AUM. Depreciation and Financial are not scaled, they stay as in this year
                if (!amounts[key] || ["UCITS", "Perenne", "AIF", "Depreciation", "Financial"].includes(category.label)) continue;

                const rate = category.group === Revenue ? revenueGrowth : costGrowth;
                const factor = Math.pow(1 + rate / 100, yearsAhead);
                amounts[key] = amounts[key].map((value) => (typeof value === "undefined" ? undefined : value * factor));
            }
            applyAdjustments(year, true);
        }

        for (const group of [Revenue, Costs]) {
            const groupValues: (number | undefined)[] = monthKeys.map((_key, index) => (hasData[index] ? 0 : undefined));

            result.push({ kind: "group", label: group });
            for (const category of categories.filter((c) => c.group === group)) {
                const values = amounts[group + "|" + category.label];
                if (!values) continue;

                const categoryRow = toRow("category", category.label, values);
                categoryRow.categoryKey = year + "|" + group + "|" + category.label;
                categoryRow.originals = monthKeys.map((_monthKey, index) => originals[`${categoryRow.categoryKey}|${index}`]);
                categoryRow.expanded = !!expandedCategories[categoryRow.categoryKey];
                result.push(categoryRow);
                if (expandedCategories[categoryRow.categoryKey] && yearsAhead === 0) {
                    const details = accountDetails[group + "|" + category.label] ?? {};
                    for (const accountNr of Object.keys(details)
                        .map(Number)
                        .sort((a, b) => a - b)) {
                        result.push({
                            kind: "account",
                            label: details[accountNr].label,
                            values: details[accountNr].values,
                            total: details[accountNr].values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
                        });
                    }
                }
                values.forEach((value, index) => {
                    if (hasData[index]) groupValues[index] = (groupValues[index] ?? 0) + (value ?? 0);
                });
            }
            groupValues.forEach((value, index) => {
                if (typeof value !== "undefined") results[index] = (results[index] ?? 0) + value;
            });
            result.push(toRow("sum", "Total " + group.toLowerCase(), groupValues));
        }

        result.push(toRow("result", "Result", results));

        let accumulated = 0;
        const accumulatedRow = toRow(
            "accumulated",
            "Accumulated result",
            results.map((value) => {
                if (typeof value === "undefined") return undefined;
                accumulated += value;
                return accumulated;
            })
        );
        accumulatedRow.total = undefined;
        result.push(accumulatedRow);

        // Capital: the equity of a month is the equity of the month before plus the result and the contribution of the month
        const contributions = monthKeys.map((_key, index) => aumEdits[`${year}|contribution|${index}`] ?? 0);
        let equityStart = openingEquity[year];
        if (typeof equityStart === "undefined" && yearsAhead === 1 && typeof openingEquity[thisYear] !== "undefined") {
            // The contributions of this year are the edits of the year of the data
            const dataYearContribution = monthKeys.reduce(
                (sum, _key, index) => sum + (aumEdits[`${dataYear}|contribution|${index}`] ?? 0),
                0
            );
            equityStart = openingEquity[thisYear] + dataYearResult + dataYearContribution;
        }

        // The capital requirement is the requirement of the year before divided by the costs of the year before, times the costs of the year
        let requirement = capitalRequirements[year];
        if (
            typeof requirement === "undefined" &&
            yearsAhead === 1 &&
            typeof capitalRequirements[thisYear] !== "undefined" &&
            dataYearCosts > 0
        ) {
            let yearCosts = 0;
            for (const [key, values] of Object.entries(amounts)) {
                if (key.startsWith(Costs + "|")) yearCosts -= values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
            }
            requirement = (capitalRequirements[thisYear] / dataYearCosts) * yearCosts;
        }
        if (typeof equityStart !== "undefined" && typeof requirement !== "undefined") {
            const requirementValue = requirement;
            let equity = equityStart;
            const equityValues = results.map((value, index) => {
                if (typeof value === "undefined") return undefined;
                equity += value + contributions[index];
                return equity;
            });

            const toCapitalRow = (kind: StatementRow["kind"], label: string, values: (number | undefined)[]): StatementRow => ({
                kind,
                label,
                values,
                total: values.filter((value) => typeof value !== "undefined").pop()
            });
            result.push({ kind: "group", label: "Capital" });
            const contributionRow = toCapitalRow(
                "capital",
                "Contribution",
                equityValues.map((value, index) => (typeof value === "undefined" ? undefined : contributions[index]))
            );
            contributionRow.fund = "contribution";
            contributionRow.total = contributions.reduce((sum, value) => sum + value, 0);
            result.push(contributionRow);
            result.push(toCapitalRow("capital", "Equity", equityValues));
            result.push(
                toCapitalRow(
                    "capital",
                    "Capital requirement",
                    equityValues.map((value) => (typeof value === "undefined" ? undefined : requirementValue))
                )
            );
            result.push(
                toCapitalRow(
                    "capitalSurplus",
                    "Surplus",
                    equityValues.map((value) => (typeof value === "undefined" ? undefined : value - requirementValue))
                )
            );
        }
        return result;
    }, [
        monthAccounts,
        dataStatuses,
        aumEdits,
        manualAdjustments,
        expandedCategories,
        revenueGrowth,
        costGrowth,
        dataNavs,
        year,
        dataYear,
        yearsAhead,
        thisYear,
        todaysDate
    ]);

    const columns = useMemo(() => {
        // Revenue and a profit are positive, a cost or a loss has a minus sign. A forecast is blue and italic
        // Sets an edit of the AUM or the contribution, null removes it
        const setEdit = (editKey: string, value: number | null): void => {
            setAumEdits((edits) => {
                if (value !== null) return { ...edits, [editKey]: value };

                const { [editKey]: _removed, ...rest } = edits;
                return rest;
            });
        };

        const renderAmount = (row: StatementRow, amount: number | undefined, monthIndex?: number): React.ReactElement => {
            if (typeof amount === "undefined") return <span />;

            const { kind } = row;
            const isForecast = typeof monthIndex !== "undefined" && monthStatuses[monthIndex] === "forecast";
            const style: React.CSSProperties = { display: "block", textAlign: "right", fontVariantNumeric: "tabular-nums" };

            if (kind === "capital" || kind === "capitalSurplus") {
                style.color = muted;
                if (kind === "capitalSurplus") {
                    style.fontWeight = 700;
                    style.color = amount < 0 ? costColor : profitColor;
                }
                if (isForecast) {
                    style.fontStyle = "italic";
                    if (kind === "capital") style.color = forecastColor;
                }

                // The contribution of a month can be edited
                if (row.fund === "contribution" && typeof monthIndex !== "undefined") {
                    return (
                        <EditableNumber
                            key={amount}
                            value={amount}
                            title="Edit the contribution"
                            style={style}
                            onCommit={(value) => setEdit(`${year}|contribution|${monthIndex}`, value)}
                        />
                    );
                }
                return <span style={style}>{numberFormat.format(amount)}</span>;
            }

            if (kind === "volume" || kind === "volumeSum") {
                style.color = muted;
                if (kind === "volumeSum") style.fontWeight = 600;
                // The AUM of the current month is the current AUM. The months after it are estimated
                const isEstimate = isForecast && new Date(Date.UTC(year, monthIndex, 1)).toISOString().substring(0, 10) > todaysDate;
                if (isEstimate) {
                    style.color = forecastColor;
                    style.fontStyle = "italic";
                }

                // The estimated AUM can be edited. An empty field goes back to the default
                if (isEstimate && kind === "volume") {
                    return (
                        <EditableNumber
                            key={amount}
                            value={amount}
                            title="Edit the AUM"
                            style={style}
                            onCommit={(value) => setEdit(`${year}|${row.fund}|${monthIndex}`, value)}
                        />
                    );
                }
                return <span style={style}>{numberFormat.format(amount)}</span>;
            }

            // 0 is not shown as −0
            const shown = amount === 0 ? 0 : amount;
            if (kind === "account") style.fontSize = "0.9em";
            if (kind === "sum" || kind === "result") style.fontWeight = 700;
            // Only a result has a color, green for a profit and red for a loss. A cost has just its minus sign
            if (kind === "result" || kind === "accumulated") {
                if (amount > 0) style.color = profitColor;
                if (amount < 0) style.color = costColor;
            }
            if (isForecast) {
                style.fontStyle = "italic";
                if (kind === "category" || kind === "sum") style.color = forecastColor;
            }
            const original = typeof monthIndex === "undefined" ? undefined : row.originals?.[monthIndex];
            const adjusted = kind === "category" && typeof original !== "undefined";
            const text = numberFormat.format(shown);
            if (kind !== "category" || typeof monthIndex === "undefined") return <span style={style}>{text}</span>;

            const adjustmentKey = `${row.categoryKey}|${monthIndex}`;
            const originalShown = Math.round(original ?? amount);
            const removeAdjustment = (): void =>
                setManualAdjustments((adjustments) => {
                    const { [adjustmentKey]: _removed, ...rest } = adjustments;
                    return rest;
                });
            return (
                <AdjustableCell
                    text={text}
                    style={style}
                    adjusted={adjusted}
                    originalText={numberFormat.format(originalShown)}
                    currentValue={shown}
                    onCommit={(entered) => {
                        // A cost is always negative, whatever sign is typed. Financial can be both, so it is kept as typed
                        let value = entered;
                        if (row.categoryKey.includes(`|${Costs}|`) && row.label !== "Financial") value = -Math.abs(entered);

                        // The original value, rounded, removes the adjustment
                        if (adjusted ? value === originalShown : value === Math.round(shown)) {
                            if (adjusted) removeAdjustment();
                        } else {
                            setManualAdjustments((adjustments) => ({ ...adjustments, [adjustmentKey]: value }));
                        }
                    }}
                    onClear={removeAdjustment}
                />
            );
        };

        const statusLabels = { actual: "ACT", preliminary: "PRE", forecast: "~" };
        const statusColors = { actual: muted, preliminary: preliminaryColor, forecast: forecastColor };

        return [
            {
                id: "label",
                header: "CATEGORY",
                accessorKey: "label",
                size: 200,
                enableSorting: false,
                enableColumnFilter: false,
                cell: (info: { getValue: () => string; row: { original: StatementRow } }) => {
                    const { kind } = info.row.original;
                    const style: React.CSSProperties = {};
                    if (kind === "group") {
                        style.color = navy;
                        style.fontSize = "0.75rem";
                        style.fontWeight = 700;
                        style.letterSpacing = "0.05em";
                        style.textTransform = "uppercase";
                    }
                    if (kind === "sum" || kind === "result") {
                        style.color = navy;
                        style.fontWeight = 700;
                    }
                    if (kind === "accumulated" || kind === "volume" || kind === "capital") style.color = muted;
                    if (kind === "capitalSurplus") style.fontWeight = 700;
                    if (kind === "account") {
                        style.color = muted;
                        style.fontSize = "0.9em";
                        style.paddingLeft = 24;
                    }
                    if (kind === "category") {
                        // Click a category to show or hide its accounts
                        const { categoryKey, expanded } = info.row.original;
                        return (
                            <span
                                style={{ ...style, cursor: "pointer" }}
                                onClick={() => setExpandedCategories((state) => ({ ...state, [categoryKey]: !state[categoryKey] }))}
                            >
                                <span style={{ color: muted, display: "inline-block", width: 14 }}>{expanded ? "▾" : "▸"}</span>
                                {info.getValue()}
                            </span>
                        );
                    }
                    if (kind === "volumeSum") {
                        style.color = muted;
                        style.fontWeight = 600;
                    }
                    return (
                        <span className={`row-${kind}`} style={style}>
                            {info.getValue()}
                        </span>
                    );
                }
            },
            ...monthKeys.map((key, index) => ({
                id: key,
                header: () => (
                    <div style={{ textAlign: "right" }}>
                        {monthNames[index]}
                        <div style={{ fontSize: "0.65rem", fontStyle: "normal", color: statusColors[monthStatuses[index]] }}>
                            {statusLabels[monthStatuses[index]]}
                        </div>
                    </div>
                ),
                accessorFn: (row: StatementRow) => row.values?.[index],
                size: 90,
                enableSorting: false,
                enableColumnFilter: false,
                cell: (info: { getValue: () => number | undefined; row: { original: StatementRow } }) =>
                    renderAmount(info.row.original, info.getValue(), index)
            })),
            {
                id: "total",
                header: () => (
                    <div style={{ textAlign: "right" }}>{monthStatuses.includes("forecast") ? "FORECAST FULL YEAR" : "FULL YEAR"}</div>
                ),
                accessorFn: (row: StatementRow) => row.total,
                size: 110,
                enableSorting: false,
                enableColumnFilter: false,
                cell: (info: { getValue: () => number | undefined; row: { original: StatementRow } }) =>
                    renderAmount(info.row.original, info.getValue())
            }
        ];
    }, [monthStatuses, year, todaysDate, setAumEdits, setManualAdjustments]);

    let content: React.ReactElement = <ReactTable columns={columns} data={rows} initialPageSize={500} />;
    if (fetching) content = <p>Loading</p>;
    if (error || errorNavs) content = <p>error: {JSON.stringify(error ?? errorNavs, null, 2)}</p>;

    return (
        <div style={{ paddingBottom: 24 }}>
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 12
                }}
            >
                <div>
                    <h1 style={{ margin: 0, fontSize: "1.25rem" }}>Income Statement {year}</h1>
                    <p style={{ margin: "2px 0 0", color: muted, fontSize: "0.85rem" }}>
                        {errorInfo
                            ? "Company info unavailable"
                            : [kleerCompanyInfo.name, kleerCompanyInfo.orgno, kleerCompanyInfo.accountingCurrency]
                                  .filter(Boolean)
                                  .join(" · ")}
                    </p>
                    <p style={{ margin: "4px 0 0", fontSize: "0.8rem", color: muted }}>
                        <strong>ACT</strong> Actual · <strong style={{ color: preliminaryColor }}>PRE</strong> Preliminary ·{" "}
                        <strong style={{ color: forecastColor }}>~</strong> Forecast (estimated). The AUM of the forecast months can be
                        edited
                        {hasAdjustments && (
                            <>
                                {" · "}
                                <span style={{ color: adjustmentColor }}>●</span> Manual adjustment
                            </>
                        )}
                    </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {hasChanges && !confirmingClear && (
                        <button
                            type="button"
                            onClick={() => setConfirmingClear(true)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                border: 0,
                                background: "transparent",
                                color: adjustmentColor
                            }}
                        >
                            <Trash2 size={14} /> Reset changes
                        </button>
                    )}
                    {hasChanges && confirmingClear && (
                        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
                            Reset all adjustments and edits of {year}?
                            <button
                                type="button"
                                onClick={() => setConfirmingClear(false)}
                                style={{ padding: "2px 10px", border: 0, borderRadius: 4, background: profitColor, color: "#fff" }}
                            >
                                No
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setManualAdjustments((adjustments) =>
                                        Object.fromEntries(Object.entries(adjustments).filter(([key]) => !key.startsWith(`${year}|`)))
                                    );
                                    setAumEdits((edits) =>
                                        Object.fromEntries(Object.entries(edits).filter(([key]) => !key.startsWith(`${year}|`)))
                                    );
                                    setConfirmingClear(false);
                                }}
                                style={{ padding: "2px 10px", border: 0, borderRadius: 4, background: costColor, color: "#fff" }}
                            >
                                Yes
                            </button>
                        </span>
                    )}
                    <select
                        value={year}
                        onChange={(event) => setYearString(event.target.value)}
                        style={{ padding: "3px 8px", border: `1px solid ${navy}`, borderRadius: 4, background: "#fff", color: navy }}
                    >
                        {selectableYears.map((y) => (
                            <option key={y} value={y}>
                                {y}
                                {y > thisYear ? " ≈" : ""}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            {yearsAhead > 0 && (
                <div
                    style={{
                        maxWidth: 760,
                        marginBottom: 16,
                        padding: "10px 14px",
                        border: "1px solid #bfdbfe",
                        borderRadius: 4,
                        background: "#eff6ff"
                    }}
                >
                    <div style={{ fontWeight: 600, color: navy, marginBottom: 6 }}>
                        Forecast {year} — based on the result of {thisYear}, scaled {yearsAhead} {yearsAhead === 1 ? "year" : "years"} ahead
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 32 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
                            Revenue growth / year
                            <input
                                type="range"
                                min={-10}
                                max={30}
                                step={0.5}
                                value={revenueGrowth}
                                onChange={(event) => setRevenueGrowth(Number(event.target.value))}
                            />
                            <strong style={{ minWidth: 44, textAlign: "right" }}>{revenueGrowth}%</strong>
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
                            Cost development / year
                            <input
                                type="range"
                                min={-10}
                                max={30}
                                step={0.5}
                                value={costGrowth}
                                onChange={(event) => setCostGrowth(Number(event.target.value))}
                            />
                            <strong style={{ minWidth: 44, textAlign: "right" }}>{costGrowth}%</strong>
                        </label>
                    </div>
                </div>
            )}
            <div className="income-statement" style={{ overflowX: "auto" }}>
                {content}
            </div>
        </div>
    );
}
